"use client";

import { track } from "@vercel/analytics";
import { useEffect, useRef, useState } from "react";

import { useMagnetUnlock } from "@/components/ownly/MagnetUnlock";

import {
  TurnstileCheck,
  TurnstileScript,
  turnstileNotReadyMessage,
  useTurnstile,
} from "@/components/ownly/TurnstileWidget";
import { campaignFields } from "@/lib/campaign-source";
import { newLeadEventId, serverLeadFields, trackLeadConversion } from "@/lib/ad-tags";
import type { LeadMagnet, LeadMagnetField } from "@/lib/lead-magnets/registry";

/**
 * The one form behind every lead magnet, driven by the registry's `fields`.
 *
 * IT DELIVERS BY EMAIL AND SAYS SO. `/audit` renders its findings on the page,
 * which is right for that page but wrong here: the whole point of a magnet is
 * that the address is worth something, and a result shown on screen makes the
 * address optional. So this confirms and sends, and the report is the email.
 *
 * Everything defensive is lifted from `SiteAuditForm`, where it is proven: an
 * off-screen honeypot, Turnstile reset in `finally` because a token is single
 * use, and marketing consent that travels with its own source so it can never
 * reach the CRM without a record of what was agreed to.
 */

const inputClass =
  "w-full rounded-2xl border border-white/[0.14] bg-ownly-surface/90 px-4 py-3.5 text-copy text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] outline-none backdrop-blur-md transition-[border-color,box-shadow] placeholder:text-white/60 focus:border-ownly-accent/55 focus:ring-2 focus:ring-ownly-accent/22";

const labelClass = "mb-2 block text-sm font-medium text-white/72";

/**
 * Prefill a url field from `?url=`, read after mount rather than on the server
 * so the markup cannot differ between the two and trip hydration.
 */
function urlFromQuery(): string {
  if (typeof window === "undefined") return "";
  const raw = new URLSearchParams(window.location.search).get("url")?.trim() ?? "";
  if (!raw || raw.length > 255) return "";
  return /^[a-z0-9.-]+\.[a-z]{2,}(\/[^\s]*)?$/i.test(raw) ? raw : "";
}

export function LeadMagnetForm({
  magnet,
  placement = "guide-page",
}: {
  magnet: LeadMagnet;
  /** Which page the form sat on, for the analytics event only. The endpoint is the same. */
  placement?: string;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [multi, setMulti] = useState<Record<string, string[]>>({});
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  /* The form unmounts on success, taking the focused button with it, so focus would drop to
     the top of the page and a screen reader would hear nothing. Move it to the confirmation. */
  const doneRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (sentTo) doneRef.current?.focus();
  }, [sentTo]);
  const { unlock } = useMagnetUnlock();
  const turnstile = useTurnstile();

  useEffect(() => {
    const prefill = urlFromQuery();
    if (!prefill) return;
    const urlField = magnet.fields.find((f) => f.kind === "url");
    if (urlField) setValues((v) => (v[urlField.name] ? v : { ...v, [urlField.name]: prefill }));
  }, [magnet.fields]);

  function setValue(name: string, value: string) {
    setValues((v) => ({ ...v, [name]: value }));
  }

  function toggleMulti(name: string, option: string) {
    setMulti((m) => {
      const current = m[name] ?? [];
      return {
        ...m,
        [name]: current.includes(option)
          ? current.filter((o) => o !== option)
          : [...current, option],
      };
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    for (const field of magnet.fields) {
      if (field.kind === "multi") continue;
      const required = field.kind === "url" || field.kind === "number" || field.kind === "choice"
        ? true
        : field.required;
      if (required && !values[field.name]?.trim()) {
        setError(`Please fill in ${field.label.toLowerCase()}.`);
        return;
      }
    }
    if (!email.trim()) {
      setError("Please add an email address so we know where to send it.");
      return;
    }
    if (!turnstile.ready) {
      // The button is disabled until the check passes, so this is only a backstop.
      setError(turnstileNotReadyMessage(turnstile.status));
      return;
    }

    setBusy(true);
    /* One id for the browser Lead and our server's copy, so Meta counts it once. */
    const leadEventId = newLeadEventId();
    try {
      const res = await fetch(`/api/free/${magnet.slug}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          ...multi,
          email: email.trim(),
          company,
          marketingOptIn: optIn,
          turnstileToken: turnstile.token,
          ...campaignFields(),
          ...serverLeadFields(leadEventId),
        }),
      });
      const data = (await res.json()) as { ok: boolean; message?: string };
      if (!data.ok) {
        setError(data.message ?? "Something went wrong at our end. Please try again in a moment.");
        return;
      }
      setSentTo(email.trim());
      // Opens the rest of the guide on the page as well as sending it, so giving us an
      // address buys something immediately rather than only in an inbox.
      unlock();
      track("lead-magnet-sent", { magnet: magnet.slug, placement });
      trackLeadConversion(`guide_${magnet.slug}`, leadEventId);
    } catch {
      setError("We could not reach our server. Check your connection and try again.");
    } finally {
      setBusy(false);
      turnstile.reset();
    }
  }

  if (sentTo) {
    return (
      <div className="rounded-2xl border border-white/[0.12] bg-ownly-surface/70 p-6 md:p-8">
        <h2 ref={doneRef} tabIndex={-1} className="ownly-display ownly-card-title text-white focus:outline-none">
          On its way.
        </h2>
        <p className="ownly-body mt-3 ownly-prose leading-[27px] text-white/85">
          {magnet.deliverable} We have sent it to{" "}
          <span className="text-white">{sentTo}</span>. If it is not there in a couple of minutes,
          check your junk folder, because a first email from a new sender often lands there.
        </p>
        <p className="ownly-body mt-4 ownly-prose leading-[27px] text-white/85">
          Reply to it with anything you want us to look at properly. A person reads those.
        </p>
      </div>
    );
  }

  return (
    <>
      <TurnstileScript />
      <form onSubmit={submit} className="rounded-2xl border border-white/[0.12] bg-ownly-surface/70 p-6 md:p-8">
        <div className="grid gap-5">
          {magnet.fields.map((field) => (
            <Field
              key={field.name}
              field={field}
              value={values[field.name] ?? ""}
              selected={multi[field.name] ?? []}
              onChange={(v) => setValue(field.name, v)}
              onToggle={(o) => toggleMulti(field.name, o)}
            />
          ))}

          <label>
            <span className={labelClass}>Where should we send it?</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbusiness.co.uk"
              name="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              className={inputClass}
              required
            />
          </label>

          <div aria-hidden className="absolute left-[-9999px] top-0 h-0 w-0 overflow-hidden">
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              name="optIn"
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
              className="mt-0.5 size-4 shrink-0 rounded border-white/25 bg-white/10 accent-ownly-accent"
            />
            <span className="ownly-prose text-body leading-[27px] text-white/85">
              {/* Per magnet, because it has to describe what actually arrives. A
                  generic "no more than monthly" next to a fortnight-long sequence
                  is consent for something we are not doing. */}
              {magnet.consent ??
                (magnet.video
                  ? "Send me a couple of follow-up emails about it. Your link is your own, so we will see whether you got to watch it and only offer a call once you have. One click stops it."
                  : "Send me the occasional email about what we build. No more than monthly, and one click stops it.")}
            </span>
          </label>

          <TurnstileCheck turnstile={turnstile} />

          {error ? (
            <div
              role="alert"
              className="rounded-xl border border-[#E05A45]/35 bg-[#E05A45]/[0.1] px-4 py-3 text-copy text-[#F2AA9D]"
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={busy || !turnstile.ready}
            className="ownly-control ownly-btn-primary inline-flex h-12 items-center justify-center rounded-full bg-ownly-accent px-6 text-white shadow-[var(--ownly-cta-shadow)] transition hover:-translate-y-0.5 hover:bg-[#3d8aff] disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "Sending it over…" : turnstile.waiting ? "Waiting for the security check" : "Send it to me"}
          </button>

          <p className="text-sm leading-[22px] text-white/60">
            We use your address to send this and nothing else unless you tick the box above. See our{" "}
            <a href="/privacy" className="text-white/65 underline underline-offset-2">
              privacy policy
            </a>
            .
          </p>
        </div>
      </form>
    </>
  );
}

function Field({
  field,
  value,
  selected,
  onChange,
  onToggle,
}: {
  field: LeadMagnetField;
  value: string;
  selected: string[];
  onChange: (v: string) => void;
  onToggle: (option: string) => void;
}) {
  if (field.kind === "multi") {
    return (
      <fieldset>
        <legend className={labelClass}>{field.label}</legend>
        <div className="flex flex-wrap gap-2">
          {field.options.map((option) => {
            const on = selected.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => onToggle(option)}
                aria-pressed={on}
                className={`rounded-full border px-4 py-2 text-copy transition ${
                  on
                    ? "border-ownly-accent/60 bg-ownly-accent/15 text-white"
                    : "border-white/[0.16] bg-white/[0.03] text-white/70 hover:border-white/30"
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (field.kind === "choice") {
    return (
      <label>
        <span className={labelClass}>{field.label}</span>
        <select name={field.name} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
          <option value="">Choose one</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.kind === "number") {
    return (
      <label>
        <span className={labelClass}>{field.label}</span>
        <div className="flex items-center gap-3">
          <input
            type="number"
            name={field.name}
            inputMode="numeric"
            min={field.min}
            max={field.max}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
          {field.suffix ? (
            <span className="shrink-0 text-copy text-white/55">{field.suffix}</span>
          ) : null}
        </div>
      </label>
    );
  }

  return (
    <label>
      <span className={labelClass}>{field.label}</span>
      <input
        type="text"
        name={field.name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        inputMode={field.kind === "url" ? "url" : undefined}
        autoCapitalize={field.kind === "url" ? "none" : undefined}
        autoCorrect={field.kind === "url" ? "off" : undefined}
        spellCheck={field.kind === "url" ? false : undefined}
        className={inputClass}
      />
    </label>
  );
}
